<script setup lang="ts">
  import { IconEstimatedTime } from '@gat-ship/icons'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { useEstimatedTimeNames } from '../../../../composables/useEstimatedTimeNames'

  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useEstimatedTimeNames()

  const referenceTimePattern = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

  const schema = z.object({
    code: z.string().min(1, 'Required').max(10, 'Max 10 characters'),
    name: z.string().min(1, 'Required').max(40, 'Max 40 characters'),
    referenceTime: z
      .string()
      .regex(referenceTimePattern, 'Use HH:mm or HH:mm:ss'),
    isInactive: z.boolean()
  })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      code: '',
      name: '',
      referenceTime: '',
      isInactive: false
    } as FormValues,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      const referenceTime =
        value.referenceTime.length === 5
          ? `${value.referenceTime}:00`
          : value.referenceTime
      await service.add({
        code: value.code,
        name: value.name,
        referenceTime,
        isInactive: value.isInactive
      })
      isOpen.value = false
    }
  })

  function showModal() {
    form.reset({
      code: '',
      name: '',
      referenceTime: '',
      isInactive: false
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEstimatedTime class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Add estimated time name
      </ModalHeaderTitle>
      <ModalHeaderDescription>
        Define a new approximate-time label (e.g. Early AM, Noon).
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="code" label="Code" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="referenceTime"
            label="Reference time"
            required
            hint="24-hour clock; drives sort order and ETA lookup."
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                placeholder="HH:mm:ss"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Add" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
